/**
 * InformationSection - Projektinformation
 * 
 * Budget, Finansiering, ROT-avdrag, Önskad start, Arbetsdagar
 */

import React from 'react';

const InformationSection = ({
  data,
  onChange,
  onBlur,
  expanded,
  onToggle,
  isRotBeloppEditable,
  onToggleRotEditable,
  // En bostadsrättsförening har inte rätt till ROT-avdrag. Då döljs ROT-fälten
  // och fastighetsbeteckningen, som bara behövs för att kunna söka ROT.
  arBrf = false
}) => {
  return (
    <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div 
        className="bg-gray-100 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-200 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-leadme-gray-dark">Information</h2>
          <svg 
            className={`w-5 h-5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className="p-4 sm:p-6 space-y-4">
          {/* Budget */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Budget
            </label>
            <input
              type="text"
              value={data.budget}
              onChange={(e) => onChange('budget', e.target.value)}
              onBlur={onBlur}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink"
            />
          </div>
          
          {/* Finansiering */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Finansiering
            </label>
            <select
              value={data.finansiering}
              onChange={(e) => { onChange('finansiering', e.target.value); onBlur?.(); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink"
            >
              <option value="">Välj...</option>
              <option value="Klar">Klar</option>
              <option value="Ska prata med banken">Ska prata med banken</option>
              <option value="Ej klar">Ej klar</option>
            </select>
          </div>
          
          {/* ROT-avdrag (st) */}
          {!arBrf && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ROT-avdrag (st)
            </label>
            <input
              type="number"
              min="0"
              value={data.rotAvdrag ?? 0}
              onChange={(e) => {
                const antal = parseInt(e.target.value) || 0;
                onChange('rotAvdrag', antal);
                // Om ROT-belopp inte är manuellt redigerat, sätt standardbelopp per person
                if (!isRotBeloppEditable) {
                  onChange('rotBelopp', 50000);
                }
              }}
              onBlur={onBlur}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink"
            />
          </div>
          )}

          {/* ROT-Belopp per person */}
          {!arBrf && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ROT-Belopp per person
            </label>
            <div className="relative">
              <input
                type="text"
                value={isRotBeloppEditable 
                  ? ((data.rotBelopp || 50000).toLocaleString('sv-SE') + ' kr')
                  : (data.rotAvdrag > 0 ? '50 000 kr' : 'Inget ROT-avdrag')
                }
                onChange={(e) => {
                  if (isRotBeloppEditable) {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    const beloppPerPerson = parseInt(value) || 0;
                    // Spara belopp PER PERSON (inte totalt)
                    onChange('rotBelopp', beloppPerPerson);
                  }
                }}
                onBlur={onBlur}
                disabled={!isRotBeloppEditable}
                className={`w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg ${
                  isRotBeloppEditable 
                    ? 'focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink' 
                    : 'bg-gray-100 text-gray-700 cursor-not-allowed'
                }`}
              />
              <button
                type="button"
                onClick={() => {
                  if (!isRotBeloppEditable) {
                    // Aktivera redigeringsläge - behåll nuvarande värde per person
                    onToggleRotEditable(true);
                    // rotBelopp är redan satt till belopp per person
                  } else {
                    // Återgå till standardläge (50 000 kr per person)
                    onToggleRotEditable(false);
                    onChange('rotBelopp', 50000);
                  }
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-leadme-pink transition-colors"
                title={isRotBeloppEditable ? 'Lås fält (återgå till standard)' : 'Redigera belopp manuellt'}
              >
                {isRotBeloppEditable ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          )}

          {/* Fastighetsbeteckning — behövs bara för att kunna söka ROT */}
          {!arBrf && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fastighetsbeteckning
            </label>
            <input
              type="text"
              value={data.propertyDesignation || ''}
              onChange={(e) => onChange('propertyDesignation', e.target.value)}
              onBlur={onBlur}
              placeholder="t.ex. Faddergåvan 10, Södermalm 1:5"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink"
            />
            <p className="text-xs text-gray-500 mt-1">
              Krävs för ROT-avdrag. Hittas i fastighetsregistret eller på bostadsrättsföreningen.
            </p>
          </div>
          )}

          {/* Önskad start */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Önskad start
            </label>
            <input
              type="text"
              value={data.önskadStart}
              onChange={(e) => onChange('önskadStart', e.target.value)}
              onBlur={onBlur}
              placeholder="t.ex. Omgående, inom 3 månader, 2024-06-01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink"
            />
          </div>
          
          {/* Antal arbetsdagar */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Antal arbetsdagar
            </label>
            <input
              type="number"
              min="0"
              value={data.workDays || ''}
              onChange={(e) => onChange('workDays', e.target.value ? parseInt(e.target.value, 10) : null)}
              onBlur={onBlur}
              placeholder="t.ex. 30, 45, 60"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink"
            />
            <p className="text-xs text-gray-500 mt-1">
              Estimerat antal arbetsdagar för projektet
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InformationSection;

